/**
 * Uniform status chip for the billing tab's date-stamped lifecycle events —
 * consent, submission, assessment. One shape everywhere: a check icon, a
 * single-word label (uppercased by the Badge), and the date inside the chip in
 * a compact `M/DD/YY` format — so the three events read consistently instead of
 * each having its own phrasing and date placement.
 *
 * @agentic connections
 */

import { Badge } from "@mantine/core";
import { IconCircleCheck } from "@tabler/icons-react";

const COMPACT_DATE_FMT = new Intl.DateTimeFormat("en-US", {
  month: "numeric",
  day: "2-digit",
  year: "2-digit",
  timeZone: "UTC",
});

/** Format an ISO date as `M/DD/YY` (UTC); `undefined` passes through. @agentic connections */
export const formatCompactDate = (
  iso: string | undefined,
): string | undefined => {
  if (!iso) return undefined;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? undefined : COMPACT_DATE_FMT.format(new Date(ms));
};

/** Props for {@link BillingStatusChip}. @agentic connections */
export type BillingStatusChipProps = {
  /** Single-word label, e.g. "Submitted" (rendered uppercase by the Badge). */
  label: string;
  /** ISO date shown inside the chip in compact form. */
  date?: string;
  /** Mantine color; defaults to green (a completed, positive event). */
  color?: string;
};

/** Date-stamped lifecycle chip (consent / submission / assessment). @agentic connections */
export const BillingStatusChip = ({
  label,
  date,
  color = "green",
}: BillingStatusChipProps) => {
  const compact = formatCompactDate(date);
  return (
    <Badge
      size="sm"
      variant="light"
      color={color}
      leftSection={<IconCircleCheck size={12} />}
    >
      {label}
      {compact ? ` ${compact}` : ""}
    </Badge>
  );
};
